import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Button, Flex, HStack, Modal, ModalBody, ModalCloseButton, ModalContent,
  ModalFooter, ModalHeader, ModalOverlay, SimpleGrid, Spinner, Text, useToast,
} from "@chakra-ui/react";
import { api } from "../../lib/api";
import Pill from "../../Components/ui/Pill";

// One person's login evidence: recent wrong passwords, last good login, last
// failure with IP and device, when their login ends, and the timeline behind
// it. Built after repeated lockouts in Oct 2026, when none of this
// could be seen without querying the database. Accounts no longer lock; the
// lock labels below are for older history.

const EVENT_LABEL = {
  "user.login": "Signed in",
  "user.login_mfa": "Signed in with 2FA",
  "user.login_failed": "Sign-in refused",
  "user.login_locked": "Locked after failed sign-ins",
  "user.session_renewed": "Extended their login",
  "user.password_changed": "Changed own password",
  "user.password_reset": "Password reset by admin",
  "user.password_reset_requested": "Asked for a password reset",
  "user.unlock": "Unlocked by admin",
  "user.force_logout": "Signed out by admin",
  "user.create": "Account created",
  "user.update": "Account edited",
  "user.invite_accepted": "Accepted invite",
  "user.mfa_enabled": "Turned on 2FA",
  "user.mfa_disabled": "Turned off 2FA",
  "user.mfa_reset_by_admin": "2FA reset by admin",
};

const REASON_LABEL = {
  wrong_password: "Wrong password",
  unknown_email: "Unknown email",
  locked: "Tried while locked",
  suspended: "Account suspended",
  mfa_invalid: "Wrong 2FA code",
  rate_limited: "Too many tries for this email",
  rate_limited_network: "Too many tries from this network",
};

const FAILURE = new Set(["user.login_failed", "user.login_locked"]);
const SUCCESS = new Set(["user.login", "user.login_mfa", "user.session_renewed"]);

function fmt(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString([], {
    weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit",
  });
}

// "Chrome on Windows" from a user-agent string. Good enough to tell a phone
// from a laptop, or spot a second device.
function device(ua) {
  if (!ua) return "";
  const os = /iPhone|iPad/.test(ua) ? "iPhone/iPad"
    : /Android/.test(ua) ? "Android"
    : /Windows/.test(ua) ? "Windows"
    : /Mac OS X|Macintosh/.test(ua) ? "Mac"
    : /Linux/.test(ua) ? "Linux" : "";
  const browser = /Edg\//.test(ua) ? "Edge"
    : /OPR\//.test(ua) ? "Opera"
    : /Firefox\//.test(ua) ? "Firefox"
    : /Chrome\//.test(ua) ? "Chrome"
    : /Safari\//.test(ua) ? "Safari" : "";
  if (!os && !browser) return ua.slice(0, 60);
  return [browser, os].filter(Boolean).join(" on ");
}

function detail(e, email) {
  const m = e.metadata || {};
  const parts = [];
  if (m.reason) parts.push(REASON_LABEL[m.reason] || m.reason);
  if (typeof m.attemptsLeft === "number") parts.push(`${m.attemptsLeft} ${m.attemptsLeft === 1 ? "try" : "tries"} left`);
  else if (typeof m.attempts === "number" && m.attempts > 1) parts.push(`${m.attempts} in a row`);
  if (e.action === "user.update") {
    const fields = Object.keys(m).filter((k) => !["email", "userAgent"].includes(k));
    if (fields.length) parts.push(`Changed ${fields.join(", ")}`);
  }
  if (e.action === "user.password_reset" && m.wasLocked) parts.push("was locked, now unlocked");
  if (e.actorEmail && e.actorEmail !== email) parts.push(`by ${e.actorEmail}`);
  return parts.join(" · ");
}

function where(e) {
  const d = device(e.metadata && e.metadata.userAgent);
  return [e.ip, d].filter(Boolean).join(" · ");
}

function Fact({ label, children, hint }) {
  return (
    <Box p={3} rounded="md" bg="bg.subtle">
      <Text fontSize="xs" color="text.secondary" mb={1}>{label}</Text>
      <Box fontSize="sm" color="text.primary" fontWeight={500}>{children}</Box>
      {hint && <Text fontSize="xs" color="text.secondary" mt={1}>{hint}</Text>}
    </Box>
  );
}

function sessionText(d) {
  switch (d.sessionState) {
    case "active": return { text: `Signed in until ${fmt(d.sessionEndsAt)}`, tone: "success" };
    case "expired": return { text: `Login expired ${fmt(d.sessionEndsAt)}`, tone: "warning" };
    case "ended": return { text: "Signed out by admin", tone: "neutral" };
    default: return { text: "No login on record", tone: "neutral" };
  }
}

function asText(d) {
  const lines = [`Login health for ${d.user.username} <${d.user.email}>`, ""];
  for (const e of d.events) {
    lines.push([fmt(e.createdAt), EVENT_LABEL[e.action] || e.action, detail(e, d.user.email), where(e)].filter(Boolean).join(" | "));
  }
  return lines.join("\n");
}

export default function LoginHealthModal({ user, isOpen, onClose }) {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      setData(await api(`/admin/users/${user._id}/login-health`));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (isOpen) { setData(null); load(); }
  }, [isOpen, load]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(asText(data));
      toast({ title: "Timeline copied", status: "success", duration: 2000 });
    } catch (e) {
      toast({ title: "Couldn't copy", status: "error" });
    }
  }

  const wrong = data && data.wrongPasswords;
  const session = data && sessionText(data);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          Login health
          {user && <Text fontSize="sm" fontWeight={400} color="text.secondary">{user.username} · {user.email}</Text>}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {loading && !data && <Spinner />}
          {error && <Text color="danger">{error}</Text>}
          {data && (
            <>
              {data.sameNameAs.length > 0 && (
                <Box mb={4} p={3} rounded="md" style={{ background: "var(--tone-warning-bg)", color: "var(--tone-warning-fg)" }}>
                  <Text fontSize="sm" fontWeight={500}>
                    Another account has the same name: {data.sameNameAs.join(", ")}. Make sure you're
                    resetting, editing or marking attendance on the one this person signs in with.
                  </Text>
                </Box>
              )}

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mb={5}>
                <Fact
                  label="Wrong passwords"
                  hint="Accounts don't lock, so a wrong password never blocks the right one. After 15 wrong tries in 15 minutes from one network, sign-in for this email pauses until those 15 minutes are up."
                >
                  {wrong.recent ? (
                    <Pill tone="warning">{wrong.recent} in the last {wrong.windowMinutes} min</Pill>
                  ) : (
                    <Pill tone="success">None recently</Pill>
                  )}
                </Fact>
                <Fact label="Login" hint="Logins last 7 days from sign-in or the last time it was extended. Shows the newest one; a login on another device may end sooner.">
                  <Pill tone={session.tone}>{session.text}</Pill>
                </Fact>
                <Fact label="Last successful sign-in" hint={data.lastLogin ? where(data.lastLogin) : null}>
                  {data.lastLogin ? fmt(data.lastLogin.createdAt) : "None on record"}
                </Fact>
                <Fact label="Last refused sign-in" hint={data.lastFailure ? [detail(data.lastFailure, data.user.email), where(data.lastFailure)].filter(Boolean).join(" · ") : null}>
                  {data.lastFailure ? fmt(data.lastFailure.createdAt) : "None on record"}
                  {data.failuresLast24h > 0 && (
                    <Text as="span" color="text.secondary" fontWeight={400}> · {data.failuresLast24h} in the last 24 hours</Text>
                  )}
                </Fact>
                <Fact label="Password last changed">
                  {data.user.passwordChangedAt ? fmt(data.user.passwordChangedAt) : "Not recorded"}
                </Fact>
                <Fact label="Account">
                  <HStack spacing={2} wrap="wrap">
                    <Pill tone={data.user.status === "active" ? "success" : "danger"}>{data.user.status}</Pill>
                    {data.user.mfaEnabled && <Pill tone="info">2FA on</Pill>}
                    <Text as="span" fontWeight={400} color="text.secondary">Last active {fmt(data.user.lastSeen)}</Text>
                  </HStack>
                </Fact>
              </SimpleGrid>

              <Flex align="center" mb={2}>
                <Text fontWeight={600} fontSize="sm" flex="1">Timeline</Text>
                <Button size="xs" variant="ghost" onClick={copy} isDisabled={!data.events.length}>Copy as text</Button>
              </Flex>
              {data.events.length === 0 && (
                <Text fontSize="sm" color="text.secondary">No login events recorded yet. Refused sign-ins are only recorded from this update onwards.</Text>
              )}
              <Box>
                {data.events.map((e) => (
                  <Flex key={e._id} py={2} gap={3} borderBottom="1px solid" borderColor="border.subtle" align="flex-start" wrap="wrap">
                    <Text fontSize="xs" color="text.secondary" minW="150px" className="tabular">{fmt(e.createdAt)}</Text>
                    <Box flex="1" minW="220px">
                      <HStack spacing={2}>
                        <Pill size="sm" tone={FAILURE.has(e.action) ? "danger" : SUCCESS.has(e.action) ? "success" : "neutral"}>
                          {EVENT_LABEL[e.action] || e.action}
                        </Pill>
                        <Text fontSize="sm">{detail(e, data.user.email)}</Text>
                      </HStack>
                      {where(e) && <Text fontSize="xs" color="text.secondary" mt={0.5}>{where(e)}</Text>}
                    </Box>
                  </Flex>
                ))}
              </Box>
            </>
          )}
        </ModalBody>
        <ModalFooter gap={2}>
          <Button variant="ghost" onClick={load} isLoading={loading}>Refresh</Button>
          <Button onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
